import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { formatXof } from '../utils/format';
import { confirmDialog } from '../utils/confirm';

const SUPPLIER_TYPES = ['Entreprise', 'Association', 'Indépendant', 'Administration', 'Partenaire', 'Autre'];
const SERVICE_CATEGORIES = ['Fournitures scolaires', 'Manuels et livres', 'Tenues scolaires', 'Restauration', 'Transport', 'Maintenance', 'Équipements', 'Pharmacie', 'Services divers'];
const STATUSES = ['Actif', 'En évaluation', 'Suspendu', 'Archivé'];
const STATUS_COLOR = { 'Actif': 'green', 'En évaluation': 'gold', 'Suspendu': 'red', 'Archivé': 'gray' };

const EMPTY = {
  supplierName: '', supplierType: 'Entreprise', contactPerson: '',
  phone: '', whatsapp: '', email: '', ifuNumber: '', address: '',
  serviceCategory: '', contractStatus: 'Actif', amountDue: 0, notes: '',
};

export default function Fournisseurs() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  function load() {
    api.get('/registry/suppliers').then((d) =>
      setRows((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })))
    ).catch(() => {});
  }

  useEffect(load, []);

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    const matchQ = !q || (r.supplierName || '').toLowerCase().includes(q) || (r.contactPerson || '').toLowerCase().includes(q);
    const matchS = !statusFilter || r.contractStatus === statusFilter;
    return matchQ && matchS;
  });

  const totalDu = rows.reduce((s, r) => s + Number(r.amountDue || 0), 0);
  const actifs = rows.filter((r) => r.contractStatus === 'Actif').length;

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi"><span>Total fournisseurs</span><strong>{rows.length}</strong></div>
        <div className="kpi green"><span>Actifs</span><strong>{actifs}</strong></div>
        <div className="kpi gold"><span>Solde dû</span><strong>{formatXof(totalDu)}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Fournisseurs</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="search-input" placeholder="Nom, contact…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 140 }}>
              <option value="">Tous statuts</option>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <button className="btn btn-sm" onClick={() => setModal({ ...EMPTY })}>+ Ajouter</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {filtered.length === 0 ? <div className="empty">Aucun fournisseur.</div> : (
            <div className="table-wrap"><table>
              <thead>
                <tr><th>Nom</th><th>Type</th><th>Catégorie</th><th>Responsable</th><th>Téléphone</th><th>IFU</th><th className="num">Solde dû</th><th>Statut</th><th></th></tr>
              </thead>
              <tbody>{filtered.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.supplierName}</b></td>
                  <td>{r.supplierType || '—'}</td>
                  <td>{r.serviceCategory || '—'}</td>
                  <td>{r.contactPerson || '—'}</td>
                  <td>{r.phone || '—'}</td>
                  <td className="mono">{r.ifuNumber || '—'}</td>
                  <td className="num" style={Number(r.amountDue) > 0 ? { color: 'var(--danger)', fontWeight: 700 } : {}}>
                    {Number(r.amountDue) > 0 ? formatXof(r.amountDue) : '—'}
                  </td>
                  <td><span className={`badge ${STATUS_COLOR[r.contractStatus] || 'gray'}`}>{r.contractStatus || '—'}</span></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn-ghost btn-sm" onClick={() => setView(r)}>Fiche</button>
                    <button className="btn-ghost btn-sm" onClick={() => setModal(r)}>Modifier</button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {modal && <FournisseurModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {view && <ViewModal item={view} onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} fields={[['Nom', 'supplierName'], ['Type', 'supplierType'], ['Catégorie', 'serviceCategory'], ['Responsable', 'contactPerson'], ['Téléphone', 'phone'], ['WhatsApp', 'whatsapp'], ['Email', 'email'], ['IFU', 'ifuNumber'], ['Adresse', 'address'], ['Solde dû', 'amountDue'], ['Statut', 'contractStatus'], ['Notes', 'notes']]} title="Fournisseur" />}
    </>
  );
}

function FournisseurModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function save() {
    if (!f.supplierName) { setError('Le nom est obligatoire.'); return; }
    setSaving(true);
    try {
      const payload = { data: f, label: f.supplierName, searchText: `${f.supplierName} ${f.contactPerson || ''} ${f.phone || ''}`.trim() };
      if (isEdit) await api.patch(`/registry/suppliers/${initial.id}`, payload);
      else await api.post('/registry/suppliers', payload);
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="fieldset-title">Identification</div>
        <div className="grid-3">
          <div className="field"><label>Nom fournisseur *</label><input value={f.supplierName} onChange={set('supplierName')} /></div>
          <div className="field"><label>Type</label>
            <select value={f.supplierType} onChange={set('supplierType')}>
              {SUPPLIER_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Catégorie de prestation</label>
            <select value={f.serviceCategory} onChange={set('serviceCategory')}>
              <option value="">—</option>
              {SERVICE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="field"><label>Responsable</label><input value={f.contactPerson} onChange={set('contactPerson')} /></div>
          <div className="field"><label>Téléphone</label><input value={f.phone} onChange={set('phone')} /></div>
          <div className="field"><label>WhatsApp</label><input value={f.whatsapp} onChange={set('whatsapp')} /></div>
          <div className="field"><label>Email</label><input type="email" value={f.email} onChange={set('email')} /></div>
          <div className="field"><label>IFU</label><input value={f.ifuNumber} onChange={set('ifuNumber')} className="mono" /></div>
          <div className="field"><label>Statut contrat</label>
            <select value={f.contractStatus} onChange={set('contractStatus')}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="field"><label>Adresse</label><textarea value={f.address} onChange={set('address')} rows={2} /></div>
        <div className="grid-3">
          <div className="field"><label>Solde dû (XOF)</label><input type="number" min="0" value={f.amountDue} onChange={set('amountDue')} /></div>
        </div>
        <div className="field"><label>Notes</label><textarea value={f.notes} onChange={set('notes')} rows={2} /></div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

function ViewModal({ item, title, fields, onClose, onEdit }) {
  if (!item) return null;
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>Fiche — {title}</h2>
        <div className="view-grid">
          {fields.map(([label, key]) => item[key] != null && item[key] !== '' ? (
            <div key={key} className="view-row">
              <span className="view-label">{label}</span>
              <span className="view-value">{String(item[key])}</span>
            </div>
          ) : null)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
